/**
 * API service functions for resources
 */

import { apiRequest, buildQueryString } from './api';
import { Resource } from '../models/Resource';
import { Category } from '../models/Category';
import { SearchState } from '../models/SearchState';
import {
  Resource as ResourceInterface,
  Category as CategoryInterface,
  PaginationInfo
} from '../types/resources';

/**
 * Response interface for paginated resources
 */
export interface ResourcesApiResponse {
  resources: ResourceInterface[];
  pagination: PaginationInfo;
  totalCount: number;
}

/**
 * Response interface for categories
 */
export interface CategoriesApiResponse {
  categories: CategoryInterface[];
}

/**
 * Response interface for individual resource
 */
export interface ResourceApiResponse {
  resource: ResourceInterface;
}

/**
 * Fetch paginated resources with search, filtering, and pagination
 */
export async function fetchResources(searchState: SearchState): Promise<ResourcesApiResponse> {
  const queryParams = searchState.toQueryParams();
  const queryString = buildQueryString(queryParams);

  try {
    const response = await apiRequest<any>(
      `/api/resources${queryString}`
    );

    // Handle different possible response structures
    let resources, pagination;

    if (response.success && response.data && Array.isArray(response.data)) {
      // Expected format from backend: { success: true, data: [], pagination: {} }
      resources = response.data;
      pagination = response.pagination;
    } else if (response.resources && Array.isArray(response.resources)) {
      // Alternative format: { resources: [], pagination: {} }
      resources = response.resources;
      pagination = response.pagination;
    } else if (Array.isArray(response)) {
      // Format: just an array of resources
      resources = response;
      pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: response.length,
        pageSize: response.length,
        hasNextPage: false,
        hasPreviousPage: false
      };
    } else if (response.data && Array.isArray(response.data)) {
      // Format: { data: [] }
      resources = response.data;
      pagination = response.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: response.data.length,
        pageSize: response.data.length,
        hasNextPage: false,
        hasPreviousPage: false
      };
    } else {
      console.warn('Unexpected response format or empty response from resources API:', response);
      resources = [];
      pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: 12,
        hasNextPage: false,
        hasPreviousPage: false
      };
    }

    if (!pagination) {
      pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: resources.length,
        pageSize: resources.length,
        hasNextPage: false,
        hasPreviousPage: false
      };
    }

    return {
      resources,
      pagination,
      totalCount: pagination.totalItems
    };
  } catch (error) {
    console.error('Error fetching resources:', error);
    throw error;
  }
}

/**
 * Fetch all available categories
 */
export async function fetchCategories(): Promise<Category[]> {
  try {
    const response = await apiRequest<any>('/api/resources/categories');

    // Handle different possible response structures
    let categories;

    if (response.success && response.data && Array.isArray(response.data)) {
      // Expected format from backend: { success: true, data: [] }
      categories = response.data;
    } else if (response.categories && Array.isArray(response.categories)) {
      // Alternative format: { categories: [] }
      categories = response.categories;
    } else if (Array.isArray(response)) {
      // Format: just an array of categories
      categories = response;
    } else if (response.data && Array.isArray(response.data)) {
      // Format: { data: [] }
      categories = response.data;
    } else {
      console.warn('Unexpected categories response format:', response);
      // Return empty array instead of throwing error to prevent page crash
      return [];
    }

    // Convert to Category model instances
    return categories.map((categoryData: CategoryInterface) => new Category(categoryData));
  } catch (error) {
    console.warn('Error fetching categories:', error);
    // Return empty array instead of throwing error to prevent page crash
    return [];
  }
}

/**
 * Fetch all unique tags from articles with resource counts
 */
export async function fetchTags(): Promise<Category[]> {
  try {
    const response = await apiRequest<any>('/api/resources/tags');

    // Handle different possible response structures
    let tags;

    if (response.success && response.data && Array.isArray(response.data)) {
      // Expected format from backend: { success: true, data: [] }
      tags = response.data;
    } else if (response.tags && Array.isArray(response.tags)) {
      // Alternative format: { tags: [] }
      tags = response.tags;
    } else if (Array.isArray(response)) {
      // Format: just an array of tags
      tags = response;
    } else if (response.data && Array.isArray(response.data)) {
      // Format: { data: [] }
      tags = response.data;
    } else {
      console.warn('Unexpected tags response format:', response);
      // Return empty array instead of throwing error to prevent page crash
      return [];
    }

    // Convert to Category model instances (tags are treated as categories for filtering)
    return tags.map((tagData: CategoryInterface) => new Category(tagData));
  } catch (error) {
    console.error('Error fetching tags:', error);
    // Return empty array instead of throwing error to prevent page crash
    return [];
  }
}

/**
 * Fetch a specific resource by slug
 */
export async function fetchResource(slug: string): Promise<Resource> {
  try {
    const response = await apiRequest<any>(
      `/api/resources/${encodeURIComponent(slug)}`
    );

    // Handle different possible response structures
    let resourceData;

    if (response.success && response.data) {
      // Expected format from backend: { success: true, data: {} }
      resourceData = response.data;
    } else if (response.resource) {
      // Alternative format: { resource: {} }
      resourceData = response.resource;
    } else if (response.data) {
      // Format: { data: {} }
      resourceData = response.data;
    } else {
      console.error('Unexpected resource response format:', response);
      throw new Error('Invalid response format: resource data is missing');
    }

    // Convert to Resource model instance
    return new Resource(resourceData);
  } catch (error) {
    console.error(`Error fetching resource ${slug}:`, error);
    throw error;
  }
}

/**
 * Search resources with a simple search term
 * This is a convenience function that creates a SearchState with just the search term
 */
export async function searchResources(searchTerm: string, page: number = 1, pageSize: number = 12): Promise<ResourcesApiResponse> {
  const searchState = new SearchState({
    searchTerm,
    currentPage: page,
    pageSize,
  });

  return fetchResources(searchState);
}

/**
 * Fetch resources by category
 * This is a convenience function that creates a SearchState with just the category filter
 */
export async function fetchResourcesByCategory(categorySlug: string, page: number = 1, pageSize: number = 12): Promise<ResourcesApiResponse> {
  const searchState = new SearchState({
    selectedCategories: [categorySlug],
    currentPage: page,
    pageSize,
  });

  return fetchResources(searchState);
}

/**
 * Fetch featured resources
 * This function fetches resources marked as featured
 */
export async function fetchFeaturedResources(limit: number = 6): Promise<Resource[]> {
  try {
    const queryString = buildQueryString({ featured: true, limit });
    const response = await apiRequest<ResourcesApiResponse>(`/api/resources${queryString}`);

    // Validate response structure
    if (!response.resources || !Array.isArray(response.resources)) {
      throw new Error('Invalid response format: resources array is missing');
    }

    // Convert to Resource model instances
    return response.resources.map((resourceData: ResourceInterface) => new Resource(resourceData));
  } catch (error) {
    console.error('Error fetching featured resources:', error);
    throw error;
  }
}

/**
 * Fetch resources with matching tags
 * @param tags Array of tags to match against
 * @param excludeResourceId ID of the current resource to exclude from results
 * @param limit Maximum number of related resources to return
 * @returns Promise with array of related resources
 */
export async function fetchRelatedResources(tags: string[], excludeResourceId: string, limit: number = 3): Promise<Resource[]> {
  if (!tags.length) {
    console.log('No tags provided for related resources');
    return [];
  }

  try {
    // First, fetch all resources
    const allResources = await fetchResources(new SearchState({ 
      pageSize: 100, // Adjust based on your expected number of resources
      currentPage: 1
    }));

    // Filter resources to find related ones
    const related = allResources.resources
      .filter(resource => 
        resource.id !== excludeResourceId && // Exclude current resource
        resource.tags && // Ensure tags exist
        resource.tags.some((tag: string) => tags.includes(tag)) // Has at least one matching tag
      )
      .map(resource => ({
        ...resource,
        matchingTags: resource.tags.filter((tag: string) => tags.includes(tag)).length
      }))
      .sort((a, b) => b.matchingTags - a.matchingTags) // Sort by number of matching tags
      .slice(0, limit) // Take top N
      .map(({ matchingTags, ...resource }) => resource); // Remove the temporary matchingTags property

    console.log('Found related resources:', related);
    return related.map(resourceData => new Resource(resourceData));

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    const errorStack = error instanceof Error ? error.stack : undefined;
    
    console.error('Error in fetchRelatedResources:', {
      error,
      message: errorMessage,
      stack: errorStack
    });
    return [];
  }
}